import { Pipe, PipeTransform } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

/**
 * Sanitizes rich-text HTML (from the Quill editor) before rendering via [innerHTML].
 *
 * Angular's built-in HTML sanitizer strips inline `style` attributes, which removes
 * Quill's text color and highlight formatting. This pipe instead whitelists safe
 * tags, attributes and CSS properties so formatting survives, while still removing
 * scripts, event handlers and dangerous URLs. The cleaned markup is then marked as
 * trusted so the preserved inline styles are not re-stripped by Angular.
 *
 * Usage: <div [innerHTML]="step.Body | safeHtml"></div>
 */
@Pipe({
  name: 'safeHtml',
  standalone: false
})
export class SafeHtmlPipe implements PipeTransform {
  private static readonly ALLOWED_TAGS = new Set([
    'P', 'BR', 'SPAN', 'DIV', 'STRONG', 'B', 'EM', 'I', 'U', 'S', 'STRIKE',
    'SUB', 'SUP', 'OL', 'UL', 'LI', 'A', 'BLOCKQUOTE', 'CODE', 'PRE',
    'H1', 'H2', 'H3', 'H4', 'H5', 'H6', 'IMG'
  ]);

  private static readonly ALLOWED_STYLE_PROPS = new Set([
    'color', 'background-color', 'text-align', 'text-decoration',
    'font-weight', 'font-style', 'font-size'
  ]);

  private static readonly ALLOWED_ATTRS = new Set([
    'class', 'target', 'rel', 'alt', 'width', 'height', 'title'
  ]);

  /** Disallowed tags whose contents are dropped entirely rather than unwrapped */
  private static readonly DROP_TAGS = new Set([
    'SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'LINK', 'META', 'NOSCRIPT'
  ]);

  constructor(private readonly sanitizer: DomSanitizer) {}

  transform(value: string | null | undefined): SafeHtml {
    if (!value) {
      return '';
    }
    const doc = new DOMParser().parseFromString(value, 'text/html');
    this.sanitizeNode(doc.body);
    return this.sanitizer.bypassSecurityTrustHtml(doc.body.innerHTML);
  }

  /** Recursively strips disallowed tags, attributes and unsafe style declarations */
  private sanitizeNode(node: Element): void {
    for (const child of Array.from(node.children)) {
      if (SafeHtmlPipe.DROP_TAGS.has(child.tagName)) {
        child.remove();
        continue;
      }
      // Clean the subtree first so unwrapping a disallowed tag can't smuggle
      // an unsanitized descendant (e.g. a nested <script>) up to the parent.
      this.sanitizeNode(child);
      if (!SafeHtmlPipe.ALLOWED_TAGS.has(child.tagName)) {
        child.replaceWith(...Array.from(child.childNodes));
      } else {
        this.cleanAttributes(child);
      }
    }
  }

  private cleanAttributes(el: Element): void {
    for (const attr of Array.from(el.attributes)) {
      const name = attr.name.toLowerCase();
      if (name.startsWith('on')) {
        el.removeAttribute(attr.name);
      } else if (name === 'style') {
        const safe = this.sanitizeStyle(attr.value);
        if (safe) { el.setAttribute('style', safe); } else { el.removeAttribute('style'); }
      } else if (name === 'href' || name === 'src') {
        if (this.isUnsafeUrl(attr.value)) { el.removeAttribute(attr.name); }
      } else if (!SafeHtmlPipe.ALLOWED_ATTRS.has(name)) {
        el.removeAttribute(attr.name);
      }
    }
  }

  /** Keeps only whitelisted CSS properties with values free of urls/expressions */
  private sanitizeStyle(style: string): string {
    return style
      .split(';')
      .map(decl => decl.trim())
      .filter(Boolean)
      .filter(decl => {
        const [prop, ...rest] = decl.split(':');
        const value = rest.join(':').trim().toLowerCase();
        return SafeHtmlPipe.ALLOWED_STYLE_PROPS.has(prop.trim().toLowerCase()) &&
          !/url\(|expression|javascript:|@import|[<>]/.test(value);
      })
      .join('; ');
  }

  private isUnsafeUrl(url: string): boolean {
    const v = url.trim().toLowerCase();
    return v.startsWith('javascript:') || v.startsWith('vbscript:') ||
      (v.startsWith('data:') && !v.startsWith('data:image/'));
  }
}
